import React, { useEffect, useState } from "react";
import env from "../../config/env.js";
import { getHeaders, criarLogAuth } from "../../utils/apiUtils";
import { verificarAcessoUsuario } from "../../utils/authStandards";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";
import {
  createApiErrorMessage,
  createValidationMessage
} from "../../utils/formatadores";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  ThumbUp,
  ThumbDown
} from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import api from "../../services/api";
import AcessoRestrito from '../../components/AcessoRestrito';

// Removendo a variável drawerWidth que não é mais necessária
// const drawerWidth = 280;

// Funções auxiliares para evitar duplicação
const obterIdEmpresa = (empresa) => {
  return empresa?.id_empresa || empresa?.id || null;
};

const validarEmpresaSelecionada = (empresaSelecionada, addError) => {
  if (!empresaSelecionada) {
    addError("Selecione uma empresa primeiro");
    return false;
  }
  const id_empresa = obterIdEmpresa(empresaSelecionada);
  if (!id_empresa) {
    addError("ID da empresa não encontrado");
    return false;
  }
  return id_empresa;
};

// Estilos compartilhados
const textFieldStyles = {
  '& .MuiInputLabel-root': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' }
  },
  '& .MuiInputBase-input': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' }
  }
};

const paperStyles = {
  flex: 1,
  overflow: 'auto',
  p: 1.5,
  boxShadow: (theme) => theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
    : '0 2px 8px rgba(0, 0, 0, 0.06)',
  borderRadius: 1,
  border: 'none'
};

const typographySmallStyles = {
  fontSize: { xs: '0.75rem', sm: '0.875rem' }
};

const typographyMediumStyles = {
  fontSize: { xs: '1rem', sm: '1.25rem' },
  fontWeight: 500,
  color: 'primary.main'
};

const formControlLabelStyles = {
  '& .MuiFormControlLabel-label': {
    fontSize: { xs: '0.75rem', sm: '0.875rem' }
  }
};

export default function EmpresaModulo() {
  // Hook do sistema de mensagens
  const { addError, addSuccess } = useSystemMessagesContext();

  const theme = useTheme();

  const usuario = JSON.parse(localStorage.getItem("usuario"));
  const st_codHashEmpresaLocal = localStorage.getItem("st_codHashEmpresa");
  const isSuperuserEmpresa1 = usuario?.perfil === "superusuario";

  const [empresas, setEmpresas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [modulosAssociados, setModulosAssociados] = useState([]);
  const [buscaEmpresa, setBuscaEmpresa] = useState("");
  const [buscaModulo, setBuscaModulo] = useState("");
  const [ordenarSelecionadosTopo, setOrdenarSelecionadosTopo] = useState(false);
  const [moduloSelecionado, setModuloSelecionado] = useState(null);
  const [modulosDisponiveis, setModulosDisponiveis] = useState([]);
  
  // Estados para seguir o padrão
  const [loading, setLoading] = useState(false);
  const [salvandoAssociacoes, setSalvandoAssociacoes] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  // Funções para seguir o padrão
  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const carregarModulosEmpresa = async (id_empresa) => {
    try {
      // Garantir que id_empresa seja um número para comparação
      const idEmpresaNumero = Number(id_empresa);
      
      if (isNaN(idEmpresaNumero)) {
        console.error("ID da empresa inválido:", id_empresa);
        setModulosAssociados([]);
        return;
      }

      const res = await api.get('/empresamodulo', {
        headers: getHeaders()
      });

      if (res.data) {
        // Filtra os módulos da empresa selecionada
        // Compara tanto id_empresa direto quanto dentro de empresa aninhado
        const modulosEmpresa = res.data.filter(em => {
          const emIdEmpresa = Number(em.id_empresa) || (em.empresa && Number(em.empresa.id_empresa));
          return emIdEmpresa === idEmpresaNumero;
        });

        // Extrai apenas os IDs dos módulos e remove duplicatas usando Set
        const modulosAssociados = [...new Set(modulosEmpresa.map(em => {
          return em.id_modulo || (em.modulo && em.modulo.id_modulo);
        }).filter(id => id != null))];

        setModulosAssociados(modulosAssociados);

        // Atualiza a lista de módulos disponíveis
        const todosModulos = res.data
          .map(em => em.modulo)
          .filter(modulo => modulo != null);
        const modulosUnicos = [...new Map(todosModulos.map(m => [m.id_modulo, m])).values()];
        setModulosDisponiveis(modulosUnicos);
      } else {
        setModulosAssociados([]);
      }
    } catch (err) {
      console.error("Erro ao carregar módulos:", err);

      if (err.code === 'ECONNABORTED') {
        addError("Tempo limite excedido ao carregar módulos");
      } else if (err.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        const errorMessage = createApiErrorMessage(err, 'Carregar módulos da empresa');
        addError(errorMessage);
      }
      setModulosAssociados([]);
    }
  };

  const handleEmpresaSelecionada = async (empresa) => {
    setEmpresaSelecionada(empresa);

    if (empresa) {
      const id_empresa = obterIdEmpresa(empresa);
      if (id_empresa) {
        await carregarModulosEmpresa(id_empresa);
      }
    }
  };

  const removerModulo = async (assoc) => {
    try {
      const id_empresa = validarEmpresaSelecionada(empresaSelecionada, addError);
      if (!id_empresa) return;

      const id_modulo = assoc.id_modulo || assoc.id;
      
      if (!id_modulo) {
        addError("ID do módulo não encontrado");
        return;
      }

      // Usa chave primária composta (id_empresa e id_modulo)
      await api.delete(`/empresamodulo/${id_empresa}/${id_modulo}`, {
        headers: getHeaders()
      });

      addSuccess("Módulo removido com sucesso!");
      
      // Recarrega os módulos da empresa
      await carregarModulosEmpresa(id_empresa);
    } catch (error) {
      console.error("Erro ao remover módulo:", error);
      const errorMessage = createApiErrorMessage(error, 'Remover módulo');
      addError(errorMessage);
    }
  };

  useEffect(() => {
    if (!isSuperuserEmpresa1) return;

    const carregarEmpresas = async () => {
      try {
        const res = await api.get('/empresas', {
          headers: getHeaders()
        });

        if (res.data) {
          setEmpresas(res.data);
        }
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
        const errorMessage = createApiErrorMessage(error, 'Carregar empresas');
        addError(errorMessage);
      }
    };

    const carregarModulos = async () => {
      try {
        const res = await api.get('/modulos', {
          headers: getHeaders()
        });

        if (res.data) {
          const modulosData = res.data;

          // Filtra os módulos baseado no perfil do usuário
          let modulosFiltrados = modulosData;
          if (usuario.perfil !== "superusuario") {
            modulosFiltrados = modulosData.filter(modulo =>
              modulo.st_codHashEmpresa === usuario.st_codHashEmpresa
            );
          }

          setModulos(modulosFiltrados);
        }
      } catch (error) {
        console.error('Erro ao carregar módulos:', error);
        const errorMessage = createApiErrorMessage(error, 'Carregar módulos');
        addError(errorMessage);
      }
    };

    carregarEmpresas();
    carregarModulos();
  }, [isSuperuserEmpresa1]);

  // Verificação de acesso - DEPOIS de todos os hooks
  if (!verificarAcessoUsuario("empresamodulo")) {
    return <AcessoRestrito modulo="empresamodulo" />;
  }


  const handleToggleModulo = async (id_modulo) => {
    try {
      const id_empresa = validarEmpresaSelecionada(empresaSelecionada, addError);
      if (!id_empresa) return;

      if (modulosAssociados.includes(id_modulo)) {
        // Remove módulo - usa chave primária composta (id_empresa e id_modulo)
        await api.delete(`/empresamodulo/${id_empresa}/${id_modulo}`, {
          headers: getHeaders()
        });
        addSuccess("Módulo removido com sucesso!");
      } else {
        // Adiciona módulo
        await api.post('/empresamodulo', {
          id_empresa: id_empresa,
          id_modulo: id_modulo
        }, {
          headers: getHeaders()
        });
        addSuccess("Módulo adicionado com sucesso!");
      }
      
      // Recarrega os módulos da empresa
      await carregarModulosEmpresa(id_empresa);
    } catch (error) {
      console.error('Erro ao alternar módulo:', error);
      const errorMessage = createApiErrorMessage(error, 'Alternar módulo');
      addError(errorMessage);
    }
  };

  const handleSalvar = async () => {
    // Prevenir múltiplos cliques
    if (salvandoAssociacoes) {
      return;
    }

    try {
      const id_empresa = validarEmpresaSelecionada(empresaSelecionada, addError);
      if (!id_empresa) return;

      // Ativar indicador de processamento
      setSalvandoAssociacoes(true);

      // Primeiro remove todos os módulos existentes da empresa
      const modulosAtuais = await api.get('/empresamodulo', {
        headers: getHeaders()
      });

      if (modulosAtuais.data) {
        const modulosEmpresa = modulosAtuais.data.filter(em => 
          (em.id_empresa === id_empresa) || 
          (em.empresa && em.empresa.id_empresa === id_empresa)
        );

        // Remove cada módulo existente usando chave primária composta
        for (const modulo of modulosEmpresa) {
          const moduloId = modulo.id_modulo || (modulo.modulo && modulo.modulo.id_modulo);
          if (moduloId) {
            await api.delete(`/empresamodulo/${id_empresa}/${moduloId}`, {
              headers: getHeaders()
            });
          }
        }
      }

      // Adiciona apenas os módulos que estão atualmente no array modulosAssociados
      for (const id_modulo of modulosAssociados) {
        await api.post('/empresamodulo', {
          id_empresa: id_empresa,
          id_modulo: id_modulo
        }, {
          headers: getHeaders()
        });
      }

      addSuccess("Associações salvas com sucesso!");
      
      // Aguardar um pouco para o usuário ver a mensagem
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Recarrega os módulos da empresa
      await carregarModulosEmpresa(id_empresa);
    } catch (error) {
      console.error('Erro ao salvar associações:', error);
      const errorMessage = createApiErrorMessage(error, 'Salvar associações');
      addError(errorMessage);
    } finally {
      // Sempre desativar o indicador de processamento
      setSalvandoAssociacoes(false);
    }
  };

  const handleImprimirContrato = () => {
    if (!empresaSelecionada) {
      addError("Selecione uma empresa primeiro");
      return;
    }

  const modulosSelecionados = modulos.filter(m => modulosAssociados.includes(m.id_modulo));
  const dataAtual = new Date().toLocaleDateString('pt-BR');
  const usuarioLogado = JSON.parse(localStorage.getItem("usuario"));
  const dataHoraAtual = new Date().toLocaleString('pt-BR');

  const contrato = `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />
        <title>Contrato de Licença de Software</title>
        <style>
          .header-info {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            text-align: center;
            font-size: 8pt;
            color: #666;
            padding: 5px;
            background-color: white;
            border-bottom: 1px solid #eee;
          }
          body {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: auto;
            padding: 40px;
            padding-top: 30px;
            line-height: 1.6;
            text-align: justify;
          }
          h1, h2 { 
            text-align: center;
            margin-bottom: 20px;
          }
          .assinaturas {
            display: flex;
            justify-content: space-between;
            margin-top: 60px;
          }
          .assinaturas div {
            text-align: center;
            width: 45%;
          }
          .logo {
            text-align: center;
            margin-bottom: 20px;
          }
          .logo img {
            max-width: 150px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            font-size: 0.9em;
            color: gray;
          }
          p {
            margin-bottom: 15px;
          }
          ul {
            margin-bottom: 15px;
          }
          li {
            margin-bottom: 5px;
          }
          @media print {
            .header-info {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              text-align: center;
              font-size: 8pt;
              color: #666;
              padding: 5px;
              background-color: white;
              border-bottom: 1px solid #eee;
            }
            body {
              padding-top: 30px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header-info">
          Impresso pelo usuário: ${usuarioLogado.st_nome} - ${usuarioLogado.st_email} em ${dataHoraAtual}
        </div>

        <div class="logo">
          <img src="/logo192.png" alt="" />
        </div>

        <h1>Contrato de Licença de Uso</h1>
        <h2>Software Modular</h2>

        <p><strong>LICENCIADA:</strong> ${empresaSelecionada.st_nome}, inscrita no CNPJ sob o nº ${empresaSelecionada.st_cnpj || '(número não cadastrado)'}.</p>

        <p><strong>OBJETO:</strong> O presente contrato tem por objeto o uso do <strong>Software Modular</strong>, desenvolvido por <strong>Samuel Alexandre de Oliveira - ME</strong>, com os seguintes módulos contratados:</p>

        <ul>
          ${modulosSelecionados.map(m => `<li>${m.st_descricao}</li>`).join('')}
        </ul>

        <div style="page-break-before: always;"></div>

        <div style="
          border-top: 1px solid #000;
          margin: 20px 0;
          width: 100%;
        "></div>

        <div style="
          font-size: 16px;
          font-weight: bold;
          text-align: center;
          margin: 20px 0;
          padding: 10px;
          border-bottom: 1px solid #ccc;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        ">
          CLÁUSULA PRIMEIRA – DO PAGAMENTO
        </div>
        <p>A licença é mensal, com pagamento até o dia 15 de cada mês, via boleto, depósito bancário sem agendamento ou PIX.</p>

        <div style="
          font-size: 16px;
          font-weight: bold;
          text-align: center;
          margin: 20px 0;
          padding: 10px;
          border-bottom: 1px solid #ccc;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        ">
          CLÁUSULA SEGUNDA – DA SUSPENSÃO
        </div>
        <p>Após 15 dias de inadimplência, o sistema será bloqueado automaticamente, retornando após a regularização.</p>

        <div style="
          font-size: 16px;
          font-weight: bold;
          text-align: center;
          margin: 20px 0;
          padding: 10px;
          border-bottom: 1px solid #ccc;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        ">
          CLÁUSULA TERCEIRA – DAS RESPONSABILIDADES
        </div>
        <p>A LICENCIADA é responsável pela guarda e sigilo das senhas e pela gestão dos dados. A LICENCIANTE garante o funcionamento do sistema, salvo falhas do provedor de hospedagem (terceirizado).</p>

        <div style="
          font-size: 16px;
          font-weight: bold;
          text-align: center;
          margin: 20px 0;
          padding: 10px;
          border-bottom: 1px solid #ccc;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        ">
          CLÁUSULA QUARTA – DO FORO
        </div>
        <p>Fica eleito o foro da Comarca de Suzano/SP.</p>

        <p><strong>Suzano,</strong> ${dataAtual}.</p>

        <div class="assinaturas">
          <div>
            ____________________________<br/>
            LICENCIADA<br/>
            ${empresaSelecionada.st_nome}
          </div>
          <div>
            ____________________________<br/>
            LICENCIANTE<br/>
            Samuel Alexandre de Oliveira - ME
          </div>
        </div>

        <div class="footer">
          <p>Contrato gerado automaticamente pelo sistema - Software Modular</p>
        </div>
      </body>
      </html>
    `;

  const janela = window.open('', '_blank');
  janela.document.write(contrato);
  janela.document.close();
  setTimeout(() => {
    janela.print();
  }, 1000);
};


  return (
    <AcessoRestrito modulo="empresamodulo">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Notificações */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!success} 
          autoHideDuration={env.NOTIFICATION_DURATION || 6000} 
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>
        
        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>

          {/* Header */}
          <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            pb: 2,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <BusinessIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107' // Mesma cor usada no menu lateral
              }} />
              Associação de Módulos
            </Typography>
          </Box>

          {/* Container Principal - Duas Colunas */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              overflow: 'hidden',
              height: {
                xs: 'calc(90vh - 140px)',  // Mobile: mais espaço para header
                sm: 'calc(90vh - 120px)',  // Tablet: espaço padrão
                md: 'calc(90vh - 120px)',  // Desktop: espaço padrão
                lg: 'calc(90vh - 120px)'   // Large: espaço padrão
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%',
              position: 'relative'
            }}
          >
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {/* Coluna Esquerda - Empresas */}
            <Box 
              sx={{ 
                flex: { xs: "1 1 100%", md: "30%" },
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Typography variant="h6" sx={typographyMediumStyles}>
                Empresas
              </Typography>
              
              <TextField
                label="Buscar Empresas"
                fullWidth
                size="small"
                value={buscaEmpresa}
                onChange={(e) => setBuscaEmpresa(e.target.value)}
                sx={textFieldStyles}
              />
              
              <Paper sx={paperStyles}>
                <RadioGroup
                  value={empresaSelecionada?.id_empresa || ""}
                  onChange={(e) => {
                    const emp = empresas.find(emp => emp.id_empresa === Number(e.target.value));
                    handleEmpresaSelecionada(emp);
                  }}
                >
                  {empresas
                    .filter((e) => e.st_nome.toLowerCase().includes(buscaEmpresa.toLowerCase()))
                    .map((empresa) => (
                    <FormControlLabel
                      key={empresa.id_empresa}
                      value={empresa.id_empresa}
                      control={<Radio />}
                      label={
                        <Typography sx={typographySmallStyles}>
                          {empresa.st_nome}
                        </Typography>
                      }
                      sx={{ 
                        mb: 0.5,
                        ...formControlLabelStyles
                      }}
                    />
                  ))}
                </RadioGroup>
              </Paper>
            </Box>

            {/* Coluna Direita - Módulos */}
            <Box 
              sx={{ 
                flex: { xs: "1 1 100%", md: "70%" },
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Typography variant="h6" sx={typographyMediumStyles}>
                  Módulos
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={ordenarSelecionadosTopo}
                      onChange={(e) => setOrdenarSelecionadosTopo(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography sx={typographySmallStyles}>
                      Ordenar selecionados no topo
                    </Typography>
                  }
                  sx={formControlLabelStyles}
                />
              </Box>
              
              <TextField
                label="Buscar Módulos"
                fullWidth
                size="small"
                value={buscaModulo}
                onChange={(e) => setBuscaModulo(e.target.value)}
                sx={textFieldStyles}
              />
              
              <Paper sx={paperStyles}>
                <List dense>
                  {modulos
                    .filter((m) => m.st_descricao.toLowerCase().includes(buscaModulo.toLowerCase()))
                    .sort((a, b) => {
                      if (!ordenarSelecionadosTopo) return a.st_descricao.localeCompare(b.st_descricao);
                      const aSelecionado = modulosAssociados.includes(a.id_modulo);
                      const bSelecionado = modulosAssociados.includes(b.id_modulo);
                      if (aSelecionado && !bSelecionado) return -1;
                      if (!aSelecionado && bSelecionado) return 1;
                      return a.st_descricao.localeCompare(b.st_descricao);
                    })
                    .map((modulo) => {
                      const IconComponent = MuiIcons[modulo.st_icone] || MuiIcons.HelpOutline;
                      return (
                        <ListItem key={modulo.id_modulo} disablePadding>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={modulosAssociados.includes(modulo.id_modulo)}
                                onChange={() => handleToggleModulo(modulo.id_modulo)}
                                size="small"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconComponent fontSize="small" />
                                <Typography sx={typographySmallStyles}>
                                  {modulo.st_descricao}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              width: '100%',
                              ...formControlLabelStyles
                            }}
                          />
                        </ListItem>
                      );
                    })}
                </List>
              </Paper>
            </Box>
          </Box>

          {/* Barra de Ações Fixa */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
              borderTop: 1,
              borderColor: 'divider',
              width: '100%',
              px: 3,
              py: 1.5,
              boxSizing: 'border-box',
              zIndex: 1000,
              minHeight: '60px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {formatarDataHora(dataHoraAtual)}
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handleImprimirContrato}
                disabled={!empresaSelecionada}
                sx={{ 
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Imprimir Contrato
              </Button>
              <Button
                variant="contained"
                onClick={handleSalvar}
                disabled={!empresaSelecionada || salvandoAssociacoes}
                startIcon={salvandoAssociacoes ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ 
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {salvandoAssociacoes ? "SALVANDO..." : "Salvar Associações"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

    </AcessoRestrito>
  );
}
